import {
  getValidLimit,
  getValidPage,
  isPageValid,
} from 'utils/tableSearchParams.util';
import type {
  BagelDocumentCategory,
  BagelDocumentDomain,
  BagelDocumentFileType,
  BagelDocumentIngestSource,
  BagelDocumentStatus,
} from '../../types';
import {
  DEFAULT_DOCUMENT_FILTERS,
  type BagelDocumentFilterState,
} from '../components/DocumentsTab/documentTableFilters';

export const BAGEL_DOCUMENTS_TABLE_DEFAULT_LIMIT = 25;
export const BAGEL_DOCUMENTS_TABLE_LIMIT_MAX = 100;

export const BAGEL_DOCUMENTS_FILTER_PARAM_KEYS = [
  'ingestSource',
  'domain',
  'category',
  'status',
  'year',
  'fileType',
] as const;

export type BagelDocumentsFilterParamKey =
  (typeof BAGEL_DOCUMENTS_FILTER_PARAM_KEYS)[number];

const SORT_COLUMNS = new Set([
  'name',
  'sourceFileLabel',
  'category',
  'docTypeLabel',
  'ingestSourceLabel',
  'ingestYear',
  'fileType',
  'status',
  'chunkCount',
  'createdOn',
]);

const INGEST_SOURCES = new Set<BagelDocumentIngestSource | 'all'>([
  'all',
  'rosie',
  'mcp',
]);

const DOMAINS = new Set<BagelDocumentDomain | 'all'>([
  'all',
  'org',
  'scla',
]);

const CATEGORIES = new Set<BagelDocumentCategory | 'all' | 'null'>([
  'all',
  'null',
  'urban_txt',
  'program',
  'procedures',
  'guidelines',
  'curriculum',
  'media',
]);

const STATUSES = new Set<BagelDocumentStatus | 'all'>([
  'all',
  'pending',
  'processing',
  'ready',
  'error',
]);

const FILE_TYPES = new Set<BagelDocumentFileType | 'all'>([
  'all',
  'pdf',
  'docx',
  'csv',
  'txt',
  'md',
  'xlsx',
]);

export type BagelDocumentsTableUrlState = {
  page: number;
  limit: number;
  sortColumn: string;
  sortType: 'asc' | 'desc';
  search: string;
  filters: BagelDocumentFilterState;
};

function getValidSortColumn(value: string | null): string {
  if (value && SORT_COLUMNS.has(value)) return value;
  return 'createdOn';
}

function getValidSortType(value: string | null): 'asc' | 'desc' {
  return value === 'asc' ? 'asc' : 'desc';
}

function getValidIngestSource(
  value: string | null,
): BagelDocumentFilterState['ingestSource'] {
  if (
    value &&
    INGEST_SOURCES.has(value as BagelDocumentIngestSource | 'all')
  ) {
    return value as BagelDocumentFilterState['ingestSource'];
  }
  return 'all';
}

function getValidDomain(
  value: string | null,
): BagelDocumentFilterState['domain'] {
  if (value && DOMAINS.has(value as BagelDocumentDomain | 'all')) {
    return value as BagelDocumentFilterState['domain'];
  }
  return 'all';
}

function getValidCategory(
  value: string | null,
): BagelDocumentFilterState['category'] {
  if (value === 'null') return 'null';
  if (
    value &&
    CATEGORIES.has(value as BagelDocumentCategory | 'all' | 'null')
  ) {
    return value as BagelDocumentFilterState['category'];
  }
  return 'all';
}

function getValidStatus(
  value: string | null,
): BagelDocumentFilterState['status'] {
  if (value && STATUSES.has(value as BagelDocumentStatus | 'all')) {
    return value as BagelDocumentFilterState['status'];
  }
  return 'all';
}

function getValidYear(value: string | null): string {
  if (!value || value === 'all') return 'all';
  return value.trim();
}

function getValidFileType(
  value: string | null,
): BagelDocumentFilterState['fileType'] {
  if (value && FILE_TYPES.has(value as BagelDocumentFileType | 'all')) {
    return value as BagelDocumentFilterState['fileType'];
  }
  return 'all';
}

export function parseBagelDocumentsTableUrlState(
  params: URLSearchParams,
): BagelDocumentsTableUrlState {
  return {
    page: getValidPage(params.get('page') || '1'),
    limit: getValidLimit(
      params.get('limit') || `${BAGEL_DOCUMENTS_TABLE_DEFAULT_LIMIT}`,
      BAGEL_DOCUMENTS_TABLE_LIMIT_MAX,
      BAGEL_DOCUMENTS_TABLE_DEFAULT_LIMIT,
    ),
    sortColumn: getValidSortColumn(params.get('sortColumn')),
    sortType: getValidSortType(params.get('sortType')),
    search: params.get('search') || '',
    filters: {
      ingestSource: getValidIngestSource(params.get('ingestSource')),
      domain: getValidDomain(params.get('domain')),
      category: getValidCategory(params.get('category')),
      status: getValidStatus(params.get('status')),
      year: getValidYear(params.get('year')),
      fileType: getValidFileType(params.get('fileType')),
    },
  };
}

export function bagelDocumentsTableStateToParams(
  state: BagelDocumentsTableUrlState,
  preserve: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(preserve);

  next.set('page', `${state.page}`);
  next.set('limit', `${state.limit}`);
  next.set('sortColumn', state.sortColumn);
  next.set('sortType', state.sortType);

  if (state.search.trim()) {
    next.set('search', state.search);
  } else {
    next.delete('search');
  }

  BAGEL_DOCUMENTS_FILTER_PARAM_KEYS.forEach(key => {
    const value = state.filters[key];
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  });

  return next;
}

export function bagelDocumentsTableNeedsUrlDefaults(
  params: URLSearchParams,
): boolean {
  if (
    params.get('page') === null ||
    !isPageValid(params.get('page') || '')
  ) {
    return true;
  }
  if (params.get('limit') === null) return true;
  if (params.get('sortColumn') === null) return true;
  if (params.get('sortType') === null) return true;

  return BAGEL_DOCUMENTS_FILTER_PARAM_KEYS.some(key => {
    const raw = params.get(key);
    if (raw === null) return false;
    if (key === 'ingestSource') return !INGEST_SOURCES.has(raw as 'all');
    if (key === 'domain') return !DOMAINS.has(raw as 'all');
    if (key === 'category') return !CATEGORIES.has(raw as 'all');
    if (key === 'status') return !STATUSES.has(raw as 'all');
    if (key === 'fileType') return !FILE_TYPES.has(raw as 'all');
    return false;
  });
}

export function buildDefaultBagelDocumentsTableParams(
  preserve: URLSearchParams,
): URLSearchParams {
  return bagelDocumentsTableStateToParams(
    {
      page: 1,
      limit: BAGEL_DOCUMENTS_TABLE_DEFAULT_LIMIT,
      sortColumn: 'createdOn',
      sortType: 'desc',
      search: '',
      filters: DEFAULT_DOCUMENT_FILTERS,
    },
    preserve,
  );
}
